// src/services/itemService.js - UPDATED VERSION
import api from '../config/axios';

const BASE_PATH = '/items';

// ============= Query Operations =============

/**
 * Get all active items (flat list)
 */
export const getAllItems = async () => {
  const response = await api.get(BASE_PATH);
  return response.data;
};

/**
 * Get all active items with pagination
 */
export const getAllItemsPaged = async (page = 0, size = 10, sort = 'itemNumber,asc') => {
  const response = await api.get(`${BASE_PATH}/paged`, {
    params: { page, size, sort }
  });
  return response.data;
};

/**
 * Get hierarchical tree structure
 */
export const getItemTree = async () => {
  const response = await api.get(`${BASE_PATH}/tree`);
  return response.data;
};

/**
 * Get item by ID
 */
export const getItemById = async (id) => {
  const response = await api.get(`${BASE_PATH}/${id}`);
  return response.data;
};

/**
 * Get item by number
 */
export const getItemByNumber = async (itemNumber) => {
  const response = await api.get(`${BASE_PATH}/number/${itemNumber}`);
  return response.data;
};

/**
 * Get item by code
 */
export const getItemByCode = async (itemCode) => {
  const response = await api.get(`${BASE_PATH}/code/${itemCode}`);
  return response.data;
};

/**
 * Get children of parent item
 */
export const getChildrenByParentId = async (parentId) => {
  const response = await api.get(`${BASE_PATH}/${parentId}/children`);
  return response.data;
};

/**
 * Get root items only
 */
export const getRootItems = async () => {
  const response = await api.get(`${BASE_PATH}/roots`);
  return response.data;
};

/**
 * Get groups only
 */
export const getGroups = async () => {
  const response = await api.get(`${BASE_PATH}/groups`);
  return response.data;
};

/**
 * Get actual items only (not groups)
 */
export const getActualItems = async () => {
  const response = await api.get(`${BASE_PATH}/actual-items`);
  return response.data;
};

/**
 * Get items by type
 */
export const getItemsByType = async (type) => {
  const response = await api.get(`${BASE_PATH}/by-type`, {
    params: { type }
  });
  return response.data;
};

/**
 * Search items by number, code, or name
 */
export const searchItems = async (query, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/search`, {
    params: { q: query, page, size }
  });
  return response.data;
};

/**
 * Advanced search with filters
 */
export const advancedSearchItems = async (filters = {}, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/advanced-search`, {
    params: {
      itemNumber: filters.itemNumber || undefined,
      itemCode: filters.itemCode || undefined,
      itemName: filters.itemName || undefined,
      itemType: filters.itemType || undefined,
      isGroup: filters.isGroup !== undefined ? filters.isGroup : undefined,
      isActive: filters.isActive !== undefined ? filters.isActive : undefined,
      page,
      size
    }
  });
  return response.data;
};

// ============= Command Operations =============

/**
 * Create new item
 */
export const createItem = async (itemData) => {
  const response = await api.post(BASE_PATH, itemData);
  return response.data;
};

/**
 * Update existing item
 */
export const updateItem = async (id, itemData) => {
  const response = await api.put(`${BASE_PATH}/${id}`, itemData);
  return response.data;
};

/**
 * Soft delete item
 */
export const softDeleteItem = async (id) => {
  await api.delete(`${BASE_PATH}/${id}`);
};

/**
 * Hard delete item (permanent)
 */
export const hardDeleteItem = async (id) => {
  await api.delete(`${BASE_PATH}/${id}/permanent`, {
    params: { confirm: true }
  });
};

/**
 * Restore soft deleted item
 */
export const restoreItem = async (id) => {
  await api.post(`${BASE_PATH}/${id}/restore`);
};

/**
 * Cleanup old deleted items
 */
export const cleanupOldDeletedItems = async (daysOld = 90) => {
  const response = await api.delete(`${BASE_PATH}/cleanup`, {
    params: { daysOld, confirm: true }
  });
  return response.data;
};

// ============= Validation Operations =============

/**
 * Check if item number exists
 */
export const checkItemNumber = async (itemNumber) => {
  const response = await api.get(`${BASE_PATH}/check/number`, {
    params: { itemNumber }
  });
  return response.data.exists;
};

/**
 * Check if item code exists
 */
export const checkItemCode = async (itemCode) => {
  const response = await api.get(`${BASE_PATH}/check/code`, {
    params: { itemCode }
  });
  return response.data.exists;
};

/**
 * Check if item number exists (excluding specific ID)
 */
export const checkItemNumberExcludingId = async (itemNumber, excludeId) => {
  const response = await api.get(`${BASE_PATH}/check/number-exclude`, {
    params: { itemNumber, excludeId }
  });
  return response.data.exists;
};

/**
 * Check if item code exists (excluding specific ID)
 */
export const checkItemCodeExcludingId = async (itemCode, excludeId) => {
  const response = await api.get(`${BASE_PATH}/check/code-exclude`, {
    params: { itemCode, excludeId }
  });
  return response.data.exists;
};

// ============= Statistics Operations =============

/**
 * Get item statistics
 */
export const getItemStatistics = async () => {
  const response = await api.get(`${BASE_PATH}/statistics`);
  return response.data;
};

// ============= Export Default =============

export default {
  getAllItems,
  getAllItemsPaged,
  getItemTree,
  getItemById,
  getItemByNumber,
  getItemByCode,
  getChildrenByParentId,
  getRootItems,
  getGroups,
  getActualItems,
  getItemsByType,
  searchItems,
  advancedSearchItems,
  createItem,
  updateItem,
  softDeleteItem,
  hardDeleteItem,
  restoreItem,
  cleanupOldDeletedItems,
  checkItemNumber,
  checkItemCode,
  checkItemNumberExcludingId,
  checkItemCodeExcludingId,
  getItemStatistics
};